import { isAuthSurface } from '@vaarta/auth-flow';
import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import {
  decideStaleSession,
  readMfaChallenge,
  submitSuitePlant,
  suiteLogout,
  suiteLogoutUrlFrom,
  suitePlantRequest,
  type MfaChallenge,
} from '@vaarta/auth-flow';

import { apiFetch, setUnauthorizedHandler } from '../lib/api';
import {
  clearSession,
  getAccessToken,
  getRefreshToken,
  getStoredUser,
  storeSession,
  storeTokens,
  type AuthUser,
} from '../lib/auth';

/**
 * Identity, as the backend defines it.
 *
 * The app does not decide whether it is protected — the deployment does, and it
 * says so at `GET /v1/auth/config`. One build therefore serves both a deployment
 * with Identity Studio wired up and one without, and neither shows a door that
 * leads nowhere. Guessing from the presence of a token would get this wrong in
 * the direction that matters: a stale token in storage would make an open
 * deployment look protected, and its absence would hide a real sign-in screen.
 */
export type AuthConfig = {
  enabled: boolean;
  loginUrl?: string;
  /**
   * Where a launcher asks which other applications this person holds (AG-1534).
   *
   * From the backend, never assembled here: `ids_suite_session` carries no `Domain`
   * attribute, so the same path on the API host answers "no shared session" forever.
   * Absent whenever this deployment does not authenticate — there is then no suite to list.
   */
  launcherUrl?: string;
  /**
   * This application's key, as Identity Studio knows it. Never a credential — it is the
   * public name of the application, and `@vaarta/auth-flow` needs it to ask which sign-in
   * plan to serve.
   */
  appKey?: string;
  /**
   * Where the sign-in page asks WHAT TO RENDER — the configured plan (AG-1802/AG-1587).
   *
   * ⚠️ From the backend, never assembled here, and the one Identity Studio URL that is on
   * the API host rather than the hosted host. It is unauthenticated XHR data read with
   * `credentials: 'omit'`, not a page the browser is sent to.
   *
   * ⛔ THE SENTENCE THIS REPLACES SAID THE HOSTED HOST 404s IT, AND THAT IS FALSE. Measured
   * on dev 2026-09-11 and again 2026-09-14: the hosted host answers **200** with the plan,
   * with `/auth/flowXX` answering 404 as the control that rules out a catch-all. Both hosts
   * serve it. The API host stays the convention — keeping unauthenticated XHR off the
   * cookie-bearing host is deliberate — but it is a convention, not a hard requirement, and
   * it must not be justified by a 404 that does not happen. ⚠️ It was load-bearing in the
   * wrong direction here in particular: Analytics' deployment has no
   * `IDENTITY_STUDIO_API_BASE_URL`, so `api_base or base` falls back and this URL is ALREADY
   * the hosted host — and works.
   *
   * Absent on a backend older than AG-1802, which is why every consumer must branch on it.
   */
  flowUrl?: string;
  /**
   * AG-1542/AG-1989 — where the BROWSER ends the shared sign-in and every sibling session.
   *
   * ⛔ From the backend, never assembled here, and on the HOSTED host. `ids_suite_session`
   * carries no `Domain`, so a call to the API host presents no cookie and clears nothing —
   * and still answers `{"ok": true}`, because logout is idempotent. The wrong URL SUCCEEDS,
   * which is the whole reason this value is not built in a browser.
   *
   * Absent on a backend older than this, which is why `suiteLogoutUrlFrom(loginUrl)` stands
   * in below rather than half a sign-out happening silently.
   */
  suiteLogoutUrl?: string;
  /**
   * ⭐ AG-1589 — whether Identity Studio says Analytics takes sign-ups, as the backend reports
   * it. `true` only for an explicit `true`: the backend answers `null` when it could not ask
   * Identity Studio, and the contract (auth-contract.md §1) says that is not a reason to offer
   * sign-up. Absent on an older backend, which reads as closed.
   */
  signupEnabled?: boolean;
};

/** What `/v1/auth/login` and `/v1/auth/sso/callback` both answer with. */
type SessionResponse = {
  access_token?: string;
  refresh_token?: string;
  user?: { id: string; email: string; name?: string };
  /** Set instead of the tokens when a second factor is wanted. */
  next_step?: string;
  methods?: string[];
  /**
   * AG-1989 — a single-use 60-second credential that buys the suite cookie, and nothing
   * else. Relayed by `charting-backend/src/auth_router.py`.
   *
   * ⚠️ Absent is ORDINARY: no sharing suite, an Identity Studio that predates the endpoint,
   * or the suite-SSO path, which attaches to a session that already has a cookie. A missing
   * token means "no suite cookie this time", never a failed sign-in.
   */
  suite_plant_token?: string;
};

/**
 * What a password sign-in did.
 *
 * `mfaRequired` is not a failure — the credentials were right and Identity Studio wants a
 * second factor. Reporting it as an error would tell somebody their password was wrong at
 * the exact moment it was proven right.
 *
 * ⚠️ IT NO LONGER MEANS "SEND THE SAME THREE FIELDS AGAIN" — AG-1805. That is what this
 * said, and it was true: the screen re-posted the credentials with a code beside them. It
 * works for an authenticator app and for NOTHING ELSE, because a delivered code only exists
 * once something has asked `mfa/send` for one against a specific challenge. `methods: string[]`
 * was all this kept of Identity Studio's answer; the `challenge_id` and the masked
 * `method_hints` were thrown away, which is why no page here could offer a channel.
 */
export type PasswordSignInResult =
  | { mfaRequired: false }
  | { mfaRequired: true; challenge: MfaChallenge };

type AuthContextValue = {
  user: AuthUser | null;
  config: AuthConfig | null;
  loading: boolean;
  /** True when this deployment does not authenticate at all. */
  open: boolean;
  isAuthenticated: boolean;
  /** Sign in on our own screen, with an email and a password. */
  signInWithPassword: (
    email: string,
    password: string,
    mfaCode?: string,
    /**
     * Where to land once the suite cookie has been collected (AG-1989). The plant hop is a
     * top-level navigation, so the page it was called from does not get to run its own
     * `navigate(next)` afterwards — the destination has to travel with the request.
     */
    returnTo?: string,
  ) => Promise<PasswordSignInResult>;
  /** Finish a sign-in that asked for a second factor — AG-1805. */
  completeMfa: (
    challengeId: string,
    method: string,
    code: string,
    returnTo?: string,
  ) => Promise<void>;
  /** Begin the Identity Studio redirect dance instead. */
  signIn: (next?: string) => Promise<void>;
  /** Finish it: redeem the one-time code. */
  completeSignIn: (code: string, state: string) => Promise<void>;
  /**
   * AG-1583 — redeem the code and hand the session BACK rather than adopting it.
   *
   * ⛔ The split exists so the callback page can name whoever the suite says is signed in
   * and wait to be asked. Adopting on arrival hands the previous person's session to
   * whoever next opens the browser, invisibly, because it looks exactly like a working
   * product. `adoptRedeemed` below is what the click calls.
   */
  redeemSignIn: (code: string, state: string) => Promise<SessionResponse>;
  adoptRedeemed: (session: SessionResponse) => void;
  /** Redeem an emailed sign-in link (AG-1805). Rejects with a reason worth showing. */
  completeMagicLink: (challengeId: string, token: string) => Promise<void>;
  logout: () => Promise<void>;
};

/**
 * This application's hosted sign-in URL — AG-1989.
 *
 * ⚠️ NOBODY IS EVER SENT HERE. It is used only to derive Identity Studio's `/sso/plant`
 * endpoint by swapping the last path segment, because it is the one URL the platform has
 * already validated against this application's registered domains. Deriving beats adding a
 * setting that can be configured wrongly, and it must be the HOSTED host: `ids_suite_session`
 * carries no `Domain`, so a plant on the API host would set a cookie nothing ever reads.
 *
 * ⛔ Returns `null` on any failure rather than throwing. A suite cookie we could not collect
 * must never turn a completed sign-in into an error.
 */
async function hostedLoginUrl(loaded: AuthConfig | null): Promise<string | null> {
  if (loaded?.loginUrl) return loaded.loginUrl;
  try {
    const out = await apiFetch<{ login_url?: string; urls?: { login?: string | null } }>(
      '/v1/auth/config',
      { authenticated: false },
    );
    return out.urls?.login || out.login_url || null;
  } catch {
    return null;
  }
}

/**
 * Where Identity Studio should drop the browser back after planting the cookie.
 *
 * ⛔ Built from `window.location.origin` and a path we have checked is a path. `next` comes
 * from a query string, so `//evil.example` and `https://evil.example` both arrive here; the
 * first is a protocol-relative URL and the second would produce a malformed one. Identity
 * Studio validates `redirect_uri` against this application's registered domains as well —
 * this is the near side of the same check, so a bad `next` degrades to the home page instead
 * of turning a sign-in into a 400.
 */
function sameOriginReturn(path: string | undefined): string {
  const home = window.location.origin + '/';
  if (!path || !path.startsWith('/') || path.startsWith('//')) return home;
  return window.location.origin + path;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * How long a successful rotation disarms the next one.
 *
 * Long enough that a burst of 401s cannot chain into repeated rotations, short
 * enough to be invisible against a token's own lifetime — these last a day.
 */
const REFRESH_COOLDOWN_MS = 10_000;

/** A public share page has no session and must never be sent to a sign-in. */
/**
 * Surfaces that must never be bounced to `/login` by a 401.
 *
 * ⚠️ `/forgot-password` and `/reset-password` joined this in AG-1804, and the reason is not
 * cosmetic: a signed-out browser can still be holding a stale token, so the `AuthProvider`
 * bootstrap's `/v1/auth/me` answers 401 and the handler below replaces the page. That would
 * throw somebody off the recovery form mid-flow — and off the reset page while holding a
 * single-use token they cannot get back — which is precisely the person least able to
 * recover from it.
 *
 * ⭐ AG-1803 — THE LIST IS NO LONGER WRITTEN HERE. It named only two of the five
 * application-owned auth surfaces: `/verify-email` and `/magic-link` were missing, and both
 * carry a single-use token the person cannot get back, so ejecting them was strictly worse
 * than ejecting them from the recovery form. The other three applications had no guard at
 * all. ⇒ `@vaarta/auth-flow.isAuthSurface` is the one set, so adding a surface fixes four
 * applications rather than one — the "fixing an instance leaves the class" rule.
 *
 * ⚠️ `/share/` stays here: a public share page is Analytics' own surface, not a suite one.
 */
function isPublicSurface(): boolean {
  const path = window.location.pathname;
  return path.startsWith('/share/') || isAuthSurface(path);
}


/** The one-shot marker for a silent resume in flight — see the comment on
 *  `refreshing` in the provider. In `sessionStorage` because the resume
 *  navigates, and a guard that does not survive the trip cannot stop it
 *  repeating (AG-2180). */
const RESUME_KEY = 'analytics_silent_resume';

function resumeAttempted(): boolean {
  try {
    return sessionStorage.getItem(RESUME_KEY) === '1';
  } catch {
    // No storage means no guard, and an unguarded resume can bounce. Report it
    // as already attempted so the person is signed out properly instead.
    return true;
  }
}

function markResumeAttempt(): void {
  try {
    sessionStorage.setItem(RESUME_KEY, '1');
  } catch {
    /* handled by `resumeAttempted` answering true */
  }
}

function clearResumeAttempt(): void {
  try {
    sessionStorage.removeItem(RESUME_KEY);
  } catch {
    /* nothing to clear */
  }
}


export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [user, setUser] = useState<AuthUser | null>(() => getStoredUser());
  const [loading, setLoading] = useState(true);
  const refreshing = useRef<Promise<boolean> | null>(null);
  /**
   * WHETHER A SILENT RESUME IS ALREADY IN FLIGHT (AG-2180).
   *
   * In `sessionStorage`, not a ref, because the resume NAVIGATES: every ref in
   * this module is gone by the time the browser comes back, and a guard that
   * does not survive the trip cannot stop the trip repeating. Cleared the moment
   * a session is adopted, so a later expiry gets its own attempt.
   *
   * One shot, and no duration anywhere: if the browser returns still unable to
   * authenticate, the marker is intact and the person is signed out properly
   * instead of bouncing.
   */
  const rotatedAt = useRef(0);

  const signOutLocally = useCallback(() => {
    clearSession();
    setUser(null);
  }, []);

  /**
   * Trade the refresh token for a new access token, at most once at a time.
   *
   * Concurrent 401s are the normal case, not the exotic one — a dashboard fires
   * a dozen tile fetches together, and an expiry lands on all of them. Without
   * the in-flight guard each would start its own rotation, and Identity Studio
   * treats a reused refresh token as theft: the first success would invalidate
   * the rest, and the family reuse detector would sign the person out of the
   * whole suite for the crime of opening a board.
   */
  const refresh = useCallback(async (): Promise<boolean> => {
    if (refreshing.current) return refreshing.current;
    const attempt = (async () => {
      const refreshToken = getRefreshToken();
      if (!refreshToken) return false;
      try {
        const out = await apiFetch<{ access_token?: string; refresh_token?: string }>(
          '/v1/auth/refresh',
          { method: 'POST', body: JSON.stringify({ refresh_token: refreshToken }), authenticated: false },
        );
        if (!out.access_token) return false;
        storeTokens(out.access_token, out.refresh_token || '');
        // REFETCH WHAT THE DEAD TOKEN TOUCHED.
        //
        // Rotating the token silently is only half a recovery: every query that
        // ran against the expired one is already settled and cached, and React
        // Query does not retry a settled query because a header changed. So the
        // session came back to life behind a screen still showing the failure —
        // most visibly "No data sources yet" for a workspace full of sources —
        // and a full page reload was the only way to see past it.
        //
        // Invalidating everything is deliberately blunt: what a dead token
        // touched is not knowable from here, and a refetch is cheap next to
        // making someone reload the app.
        rotatedAt.current = Date.now();
        void qc.invalidateQueries();
        return true;
      } catch {
        return false;
      } finally {
        refreshing.current = null;
      }
    })();
    refreshing.current = attempt;
    return attempt;
  }, [qc]);

  const signIn = useCallback(async (next?: string) => {
    const { authorize_url } = await apiFetch<{
      authorize_url: string;
      login_url: string;
    }>('/v1/auth/sso/start', { authenticated: false });
    if (next) {
      try {
        sessionStorage.setItem('analytics_sign_in_next', next);
      } catch {
        /* the redirect still works; it just lands on Home */
      }
    }

    // ⛔⛔ `login_url` IS NO LONGER A FALLBACK (AG-1989), and it was the last live route to
    // an Identity Studio page in this application. This read
    // `window.location.assign(authorize_url || login_url)` — and `login_url` is the HOSTED
    // SIGN-IN PAGE. A hosted-page navigation hidden behind a `||` is exactly the kind AG-1989
    // was filed to remove, and it survived the sweep that removed the buttons because it is
    // not a button: nothing renders it, so nothing looked wrong.
    //
    // ⚠️ It fires only when `authorize_url` comes back empty — a misconfigured or
    // mid-deploy backend — which is precisely when a person would be dropped onto another
    // product's sign-in page with no explanation.
    //
    // ⭐ `authorize_url` itself is NOT a page: `/app/{key}/sso/continue` is a redirect
    // endpoint that answers 302 either way and renders nothing. That is the automatic SSO the
    // rule permits — the suite signs you in because you have access, not because you pressed
    // something.
    //
    // ⇒ With no authorize URL there is nothing to try, so this throws and the caller shows
    // our own form. Every call site already handles that: `/login`'s probe catches and calls
    // `stopAskingInThisTab()`, and the callback page reports it.
    if (!authorize_url) {
      throw new Error('Sign-in is not configured on this deployment.');
    }
    window.location.assign(authorize_url);
  }, []);

  // What a rejected session means. Try once to renew it; if that fails the
  // session is genuinely over and the person is returned to the door — unless
  // they are on a public share page, which never had a session to lose.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      void (async () => {
        // A 401 arriving moments after a SUCCESSFUL rotation is not about the
        // token we just minted, so rotating again cannot help — and doing it
        // anyway is a loop. That loop is not hypothetical: a sibling
        // application's 401 was reaching this handler, the rotation kept
        // succeeding because the session was fine, every query refetched, and
        // the round trips added up to thousands of calls against Identity
        // Studio in minutes (AG-1273). AG-1274 removes the misread at source;
        // this makes any future one cost nothing.
        if (Date.now() - rotatedAt.current < REFRESH_COOLDOWN_MS) return;
        if (await refresh()) return;

        // ROTATION IS NOT THE ONLY WAY BACK (AG-2180).
        //
        // `refresh()` needs a refresh token, and Identity Studio does not always
        // issue one — measured on dev 2026-09-17: four sign-ins and ZERO calls
        // to `/v1/auth/refresh` in the whole retained log, because there was
        // never a token to send. Every one of those sessions ended at a login
        // form, mid-work, and the person typed their password again.
        //
        // But the browser still holds the SUITE session this sign-in planted,
        // and `/sso/start`'s `authorize_url` is the silent path: a browser that
        // has one "comes straight back with a code and never sees a form". That
        // is a way back that does not depend on a refresh token existing, and it
        // was sitting unused while people were sent to `/login?expired=1`.
        //
        // If there is no suite session either, Identity Studio answers
        // `?sso=no_shared_session` and the callback forwards to the real login —
        // so the unauthenticated case still ends where it should, by a route
        // that checked first rather than assuming.
        if (!isPublicSurface() && !window.location.pathname.startsWith('/login') && !resumeAttempted()) {
          markResumeAttempt();
          try {
            await signIn(window.location.pathname + window.location.search);
            return;
          } catch {
            // Sign-in is not configured, or `/sso/start` is unreachable. Fall
            // through to the sign-out below, which is what used to happen
            // unconditionally.
          }
        }

        signOutLocally();
        if (!isPublicSurface() && !window.location.pathname.startsWith('/login')) {
          const next = encodeURIComponent(window.location.pathname + window.location.search);
          window.location.replace(`/login?expired=1&next=${next}`);
        }
      })();
    });
    return () => setUnauthorizedHandler(null);
  }, [refresh, signOutLocally, signIn]);

  // Ask the deployment what it is, then confirm who we are.
  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      let cfg: AuthConfig = { enabled: false };
      try {
        const out = await apiFetch<{
          enabled?: boolean;
          login_url?: string;
          app_key?: string | null;
          urls?: {
            launcher?: string | null;
            flow?: string | null;
            suite_logout?: string | null;
          };
          signup_enabled?: boolean | null;
        }>('/v1/auth/config', {
          authenticated: false,
        });
        cfg = {
          enabled: Boolean(out.enabled),
          loginUrl: out.login_url,
          // AG-1583's normalised `urls` block, which this hook did not read before. Optional
          // throughout: an older backend answers without it and the launcher stays hidden,
          // which is the same rendering as a deployment with no siblings.
          launcherUrl: out.urls?.launcher || undefined,
          appKey: out.app_key || undefined,
          flowUrl: out.urls?.flow || undefined,
          suiteLogoutUrl: out.urls?.suite_logout || undefined,
          signupEnabled: out.signup_enabled === true,
        };
      } catch {
        // An unreachable backend is not a reason to lock the app: the shell
        // shows its own connection notice, and a gate we cannot confirm is a
        // gate we should not invent.
        cfg = { enabled: false };
      }
      if (cancelled) return;
      setConfig(cfg);

      if (!cfg.enabled) {
        // Open deployment. `/me` still names the workspace for the top bar.
        try {
          const me = await apiFetch<Record<string, unknown>>('/v1/auth/me');
          if (!cancelled) {
            setUser({
              id: String(me.id ?? 'local'),
              email: String(me.email ?? ''),
              name: String(me.full_name ?? '') || undefined,
            });
          }
        } catch {
          /* the shell reports the connection; do not block on a name */
        }
        if (!cancelled) setLoading(false);
        return;
      }

      if (!getAccessToken()) {
        if (!cancelled) setLoading(false);
        return;
      }
      try {
        const me = await apiFetch<Record<string, unknown>>('/v1/auth/me');
        if (!cancelled) {
          const identity = {
            id: String(me.id ?? ''),
            email: String(me.email ?? ''),
            name: String(me.full_name ?? '') || undefined,
          };
          setUser(identity);
          // ⛔⛔ AG-1788 — IS THIS TOKEN STILL THE PERSON THE SUITE SAYS IS SIGNED IN?
          // Measured in Automation Studio 2026-09-10: a token left in `localStorage` by an
          // earlier session on that browser profile was served unchecked, and the
          // application rendered another person's data. Analytics holds the same shape.
          //
          // ⚠️ ANALYTICS IS THE ONE WHERE THIS WILL USUALLY NO-OP TODAY, and that is
          // correct rather than broken: its CSP blocks `connect-src` to the Identity Studio
          // origin (AG-1845), so the probe cannot be made and `decideStaleSession` answers
          // `keep`. Wiring it now means the check starts working the moment that one
          // `nginx.conf` line lands, instead of being remembered then.
          await checkAgainstTheSuite(identity);
        }
      } catch {
        // A 401 already went through the unauthorized handler above, which
        // refreshes or signs out. Anything else is transient and must not.
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    /**
     * AG-1788's comparison, and the only thing in this effect that can end a live session.
     *
     * ⛔ FAIL SOFT — `keep` for every probe that could not be made. Identity Studio 502s its
     * siblings for about a minute on every deploy, and Analytics' own CSP blocks this origin
     * outright today. Signing people out on either is a worse failure than the one this
     * exists to fix.
     */
    async function checkAgainstTheSuite(local: { id: string; email: string }) {
      let probe: { shared?: boolean | null; user?: { sub?: string | null; email?: string | null } | null } | null = null;
      try {
        const start = await apiFetch<{ session_url?: string }>('/v1/auth/sso/start', {
          authenticated: false,
        });
        if (start.session_url) {
          const answer = await fetch(start.session_url, { credentials: 'include' });
          if (answer.ok) probe = await answer.json();
        }
      } catch {
        // `probe` stays null, which `decideStaleSession` reads as "could not ask".
      }

      const verdict = decideStaleSession({
        stored: { sub: local.id, email: local.email },
        probe,
      });
      if (verdict.action === 'keep') return;

      // ⚠️ Logged, always. A session that vanishes with no line in the console is the
      // hardest kind of bug for somebody to report, and this runs on every load.
      console.warn(`[auth] local session dropped — ${verdict.reason}`);
      signOutLocally();
      if (!cancelled) setUser(null);
      if (verdict.action === 'replace' && typeof window !== 'undefined') {
        // Re-enter the silent leg rather than leaving them anonymous: the suite HAS named
        // somebody, and `/login` is where that is picked up.
        window.location.replace('/login');
      }
    }

    void bootstrap();
    return () => {
      cancelled = true;
    };
    // `signOutLocally` is a `useCallback([])` — permanently stable, so naming it
    // here re-runs nothing. It is named because the rule is right that an effect
    // reaching for a binding should say so, and an `eslint-disable` would have
    // hidden the day that callback grows a dependency and this bootstrap starts
    // closing over a stale one.
  }, [signOutLocally]);


  /** Take a session bundle from either sign-in route and become that person. */
  const adopt = useCallback((out: SessionResponse) => {
    if (!out.access_token || !out.user) {
      throw new Error('That sign-in could not be completed.');
    }
    // A session exists again, so the next expiry is entitled to its own silent
    // resume (AG-2180).
    clearResumeAttempt();
    const signedIn: AuthUser = {
      id: out.user.id,
      email: out.user.email,
      name: out.user.name || undefined,
    };
    storeSession({
      accessToken: out.access_token,
      refreshToken: out.refresh_token || '',
      user: signedIn,
    });
    setUser(signedIn);
  }, []);

  /**
   * Adopt the session this sign-in earned, and collect the suite cookie with it.
   *
   * ⭐ EXTRACTED BY AG-1805, and the extraction is the point rather than tidiness. There are
   * now TWO ways a password sign-in completes — with a second factor and without — and a
   * second copy of this tail is how one of them quietly stops planting the suite cookie.
   * That is the AG-1989 defect exactly, and it took a person typing a password to find it.
   */
  const adoptAndPlant = useCallback(
    async (out: SessionResponse, returnTo?: string) => {
      adopt(out);

      // ⭐⭐ AG-1989 — COLLECT THE SUITE COOKIE THIS SIGN-IN JUST EARNED.
      //
      // ⛔ Identity Studio discards the suite-cookie directive when there is no browser
      // response to set it on, which is exactly what our backend's server-side `sdk_login`
      // is. The suite session ROW exists; the cookie was thrown away. So signing in here used
      // to produce a suite session the person could never present — and the only way to get a
      // usable one was to type a password on an Identity Studio page, which is why this
      // application had a "Continue with Vaarta" button pointing at one.
      //
      // ⚠️ THIS NAVIGATES AWAY. The form POST replaces the page and Identity Studio redirects
      // straight back, signed in and now holding the suite cookie. Nothing of Identity Studio
      // renders — no page, no branding, no interaction.
      //
      // ⛔ And a missing token is ORDINARY, never a failure: it means this application is in
      // no sharing suite, or Identity Studio predates the endpoint. The person is signed in to
      // THIS application either way, so we simply return as before and the caller navigates.
      const plant = suitePlantRequest({
        token: out.suite_plant_token,
        loginUrl: await hostedLoginUrl(config),
        returnTo: sameOriginReturn(returnTo),
      });
      submitSuitePlant(plant);
  },
    [adopt, config],
  );

  /**
   * Sign in without leaving analytics.
   *
   * The password goes to our backend and no further into this app than that —
   * it is not stored, not kept in state past the request, and not what comes
   * back. Identity Studio still judges it, applies the application's policy and
   * counts the failures, so the account lockout is the suite's one and not a
   * per-app imitation of it.
   */
  const signInWithPassword = useCallback(
    async (
      email: string,
      password: string,
      mfaCode?: string,
      returnTo?: string,
    ): Promise<PasswordSignInResult> => {
      const out = await apiFetch<SessionResponse>('/v1/auth/login', {
        method: 'POST',
        body: JSON.stringify({
          email: email.trim(),
          password,
          ...(mfaCode?.trim() ? { mfa_code: mfaCode.trim() } : {}),
        }),
        authenticated: false,
      });
      // ⭐ AG-1805 — the WHOLE challenge, read in the package so the four applications
      // cannot drift about what a challenge is.
      const challenge = readMfaChallenge(out);
      if (challenge) return { mfaRequired: true, challenge };

      await adoptAndPlant(out, returnTo);
      return { mfaRequired: false };
    },
    [adoptAndPlant],
  );

  /**
   * Complete a sign-in with the second factor — AG-1805.
   *
   * ⛔ Goes to `/v1/auth/mfa/verify` with the CHALLENGE, never back to `/v1/auth/login` with
   * the password again. A delivered code belongs to the challenge `mfa/send` was asked
   * against; a fresh login call mints a different one it does not belong to.
   */
  const completeMfa = useCallback(
    async (challengeId: string, method: string, code: string, returnTo?: string) => {
      await adoptAndPlant(
        await apiFetch<SessionResponse>('/v1/auth/mfa/verify', {
          method: 'POST',
          body: JSON.stringify({ challenge_id: challengeId, method, code }),
          authenticated: false,
        }),
        returnTo,
      );
    },
    [adoptAndPlant],
  );


  const redeemSignIn = useCallback(
    async (code: string, state: string) =>
      apiFetch<SessionResponse>('/v1/auth/sso/callback', {
        method: 'POST',
        body: JSON.stringify({ code, state }),
        authenticated: false,
      }),
    [],
  );

  const completeSignIn = useCallback(
    async (code: string, state: string) => {
      adopt(await redeemSignIn(code, state));
    },
    [adopt, redeemSignIn],
  );

  /**
   * Redeem an emailed sign-in link (AG-1805).
   *
   * ⭐ Through the same `adopt` every other way in uses, so a magic-link session is stored,
   * refreshed and signed out exactly like a password one. A second storage path is how two
   * notions of "signed in" start to disagree.
   */
  const completeMagicLink = useCallback(
    async (challengeId: string, token: string) => {
      adopt(
        await apiFetch<SessionResponse>('/v1/auth/magic-link/verify', {
          method: 'POST',
          body: JSON.stringify({ challenge_id: challengeId, token }),
          authenticated: false,
        }),
      );
    },
    [adopt],
  );

  const logout = useCallback(async () => {
    const refreshToken = getRefreshToken();
    signOutLocally();

    // ⭐⭐ AG-1542/AG-1989 — END THE SHARED SIGN-IN, FROM THE BROWSER.
    //
    // ⛔ The line below was already labelled "suite-wide", and it was half true. `sdk_logout`
    // does revoke the suite session and every sibling — but it clears the suite COOKIE only
    // `if response is not None`, and our backend's server-side SDK call is exactly the case
    // where it is None. Same line that caused AG-1989, opposite direction: the plant bug was
    // a cookie never set, this was a cookie never cleared. So "sign out" left the browser
    // holding a suite credential.
    //
    // ⛔ FIRST, and from the browser: only the browser holds `ids_suite_session`, and only a
    // response the browser reads can carry a `Set-Cookie` that clears it. It never throws, so
    // an unreachable Identity Studio cannot turn "sign out" into a button that does nothing.
    await suiteLogout(config?.suiteLogoutUrl || suiteLogoutUrlFrom(config?.loginUrl));

    try {
      // And the application's own session, which the suite hop above does not hold a refresh
      // token for. Best-effort: the local session is already gone.
      await apiFetch('/v1/auth/logout', {
        method: 'POST',
        body: JSON.stringify({ refresh_token: refreshToken || '' }),
        authenticated: false,
      });
    } catch {
      /* ignore — see above */
    }
  }, [signOutLocally, config]);

  const open = config !== null && !config.enabled;

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      config,
      loading,
      open,
      // An open deployment has no gate, so everyone is "authenticated" for the
      // purpose of showing a screen. `config.enabled` is the honest question and
      // is what anything security-shaped should read.
      isAuthenticated: open || Boolean(user && getAccessToken()),
      signInWithPassword,
      completeMfa,
      completeMagicLink,
      redeemSignIn,
      adoptRedeemed: adopt,
      signIn,
      completeSignIn,
      logout,
    }),
    [user, config, loading, open, signInWithPassword, completeMfa, completeMagicLink, redeemSignIn, adopt, signIn, completeSignIn, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return context;
}
